import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signUp, getCurrentSession } from '../../services/authService';
import { isValidEmail, validatePassword } from '../../utils/validation';
import './Auth.css';

/**
 * Register component for user registration
 * Allows new users to create an account with email and password
 * Profile completion will be a separate step after email verification
 */
const Register = () => {
  // Auth fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userType, setUserType] = useState('artist'); // 'artist' or 'venue'
  
  // Form state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Field validation state
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordStrength, setPasswordStrength] = useState({
    isValid: false,
    message: ''
  });
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { session } = await getCurrentSession();
      if (session) {
        navigate('/');
      }
    };

    checkSession();
  }, [navigate]);

  // Validate email as user types
  const validateEmail = (email) => {
    if (!email) {
      return 'Email is required';
    }
    if (!isValidEmail(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  // Validate password as user types
  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength({ isValid: false, message: '' });
      return 'Password is required';
    }
    
    const result = validatePassword(password);
    setPasswordStrength(result);
    return result.isValid ? '' : result.message;
  };

  // Validate confirm password as user types
  const validateConfirmPassword = (confirmPwd) => {
    if (!confirmPwd) {
      return 'Please confirm your password';
    }
    if (confirmPwd !== password) {
      return 'Passwords do not match';
    }
    return '';
  };

  // Handle input changes with validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setValidationErrors(prev => ({
      ...prev,
      email: validateEmail(value)
    }));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setValidationErrors(prev => ({
      ...prev,
      password: checkPasswordStrength(value),
      confirmPassword: confirmPassword ? validateConfirmPassword(confirmPassword) : ''
    }));
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setValidationErrors(prev => ({
      ...prev,
      confirmPassword: validateConfirmPassword(value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submission
    const emailError = validateEmail(email);
    const passwordError = checkPasswordStrength(password);
    const confirmPasswordError = validateConfirmPassword(confirmPassword);
    
    const newValidationErrors = {
      email: emailError,
      password: passwordError,
      confirmPassword: confirmPasswordError
    };
    
    setValidationErrors(newValidationErrors);
    
    // Check if there are any validation errors
    if (emailError || passwordError || confirmPasswordError) {
      setError('Please fix the validation errors before submitting');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Only include basic user metadata in step 1
      const userMetadata = {
        user_type: userType,
        profile_completed: false, // Flag to track if profile is completed
        profile_setup_required: true // Flag to indicate profile setup is required after verification
      };
      
      // Attempt to sign up with minimal information - no profile data yet
      const { error } = await signUp(email, password, userType, null, userMetadata);
      
      if (error) throw error;
      
      // Show success alert
      alert('Account Created! Please check your email to confirm your account. After confirming your email, you will be required to complete your profile before accessing the app.');
      
      // Redirect to login page with success message
      navigate('/login', { 
        state: { 
          message: 'Registration successful! Please check your email to confirm your account. After confirming your email, you will be required to complete your profile before accessing the app.' 
        } 
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Create an Account</h2>
          <p>Join Unissn to connect with gigs and venues</p>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <h3>Account Information</h3>
          <p className="info-text">
            <strong>Step 1 of 2:</strong> Create your account with basic information.<br />
            <strong>Step 2:</strong> After email verification, you'll be required to complete your profile with additional details before you can access the app.
          </p>
          
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              type="email"
              id="email"
              className={`form-input ${validationErrors.email ? 'input-error' : ''}`}
              value={email}
              onChange={handleEmailChange}
              onBlur={() => setValidationErrors(prev => ({ ...prev, email: validateEmail(email) }))}
              placeholder="Enter your email"
              required
            />
            {validationErrors.email && <div className="error-message">{validationErrors.email}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">Password</label>
            <input
              type="password"
              id="password"
              className={`form-input ${validationErrors.password ? 'input-error' : ''}`}
              value={password}
              onChange={handlePasswordChange}
              onBlur={() => setValidationErrors(prev => ({ ...prev, password: checkPasswordStrength(password) }))}
              placeholder="Create a password"
              required
            />
            {password && passwordStrength.message && (
              <div className={`password-strength ${passwordStrength.isValid ? 'valid' : 'invalid'}`}>
                {passwordStrength.message}
              </div>
            )}
            <small className="form-text">
              Password must be at least 8 characters long, include an uppercase letter, a number, and a special character
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              className={`form-input ${validationErrors.confirmPassword ? 'input-error' : ''}`}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              onBlur={() => setValidationErrors(prev => ({ ...prev, confirmPassword: validateConfirmPassword(confirmPassword) }))}
              placeholder="Confirm your password"
              required
            />
            {validationErrors.confirmPassword && <div className="error-message">{validationErrors.confirmPassword}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">I am a:</label>
            <div className="user-type-selection">
              <label className="user-type-option">
                <input
                  type="radio"
                  name="userType"
                  value="artist"
                  checked={userType === 'artist'}
                  onChange={() => setUserType('artist')}
                />
                <span>Musician/Artist</span>
              </label>
              <label className="user-type-option">
                <input
                  type="radio"
                  name="userType"
                  value="venue"
                  checked={userType === 'venue'}
                  onChange={() => setUserType('venue')}
                />
                <span>Venue/Organizer</span>
              </label>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="auth-links">
            <p>
              Already have an account? <Link to="/login">Log In</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
